import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewChild, output } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { reverseCompareResult } from '../../../common/utils/utils';
import { VersionCompareHelper } from '../../../common/helpers/version-compare.helper';

import { BundleGroup } from '../bundle-group.model';
import { ParsedBundle } from '../bundle.model';

@Component({
    selector: 'adm4-bundle-management-table',
    templateUrl: './bundle-management-table.component.html',
    styleUrls: ['./bundle-management-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('previousVersionBundlesExpand', [
            transition(':enter', [
                style({ height: '0px', minHeight: '0' }),
                animate(150, style({ height: '*' }))
            ]),
            transition(':leave', [
                style({ height: '*' }),
                animate(150, style({ height: '0px', minHeight: '0' }))
            ])
        ]),
    ],
    standalone: false
})
export class BundleManagementTableComponent implements OnChanges, AfterViewInit {
  @Input() bundleGroups: BundleGroup[];
  @Input() hasSuperAdmin: boolean;

  deleteBundle = output<ParsedBundle>();

  @ViewChild(MatSort, {static: false}) sort: MatSort;

  tableDataSource: MatTableDataSource<BundleGroup> = new MatTableDataSource([]);

  expandedBundleGroupKeys: string[] = [];

  readonly bundleNameColumnName = 'bundleName';
  readonly versionColumnName = 'version';
  readonly actionsColumnName = 'actions';
  readonly previousBundleVersionsColumnName = 'previousBundleVersions';

  displayedColumns = [this.bundleNameColumnName, this.versionColumnName, this.actionsColumnName];
  expandableColumns = [this.previousBundleVersionsColumnName];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bundleGroups) {
      this.tableDataSource.data = this.bundleGroups;
    }
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
    this.tableDataSource.sortData = this.bundleGroupSorting();
  }

  private bundleGroupSorting(): (data: BundleGroup[], sort: MatSort) => BundleGroup[] {
    return (data: BundleGroup[], sort: MatSort) => {
      switch (sort.active) {
        case this.bundleNameColumnName:
          return data.sort((bg1: BundleGroup, bg2: BundleGroup) => {
            const compareResult = bg1.latestBundle.symbolicName.localeCompare(bg2.latestBundle.symbolicName);
            return sort.direction === 'asc' ? compareResult : reverseCompareResult(compareResult);
          });
        case this.versionColumnName:
          return data.sort((bg1: BundleGroup, bg2: BundleGroup) => {
            const compareResult = VersionCompareHelper.compare(bg1.latestBundle.version, bg2.latestBundle.version);
            return sort.direction === 'asc' ? compareResult : reverseCompareResult(compareResult);
          });
        default:
          return data;
      }
    };
  }

  isGroupExpanded(bundleGroup: BundleGroup): boolean {
    return this.expandedBundleGroupKeys.some((groupKey: string): boolean => groupKey === bundleGroup.latestBundle.symbolicName);
  }

  groupHasPreviousVersionBundles(bundleGroup: BundleGroup): boolean {
    const previousVersionBundles: ParsedBundle[] = bundleGroup.previousVersionBundles;
    return Array.isArray(previousVersionBundles) && previousVersionBundles.length > 0;
  }

  toggleBundleGroup(bundleGroup: BundleGroup): void {
    if (this.isGroupExpanded(bundleGroup)) {
      this.expandedBundleGroupKeys = this.expandedBundleGroupKeys.filter((groupKey: string) => groupKey !== bundleGroup.latestBundle.symbolicName);
    } else {
      this.expandedBundleGroupKeys = this.expandedBundleGroupKeys.concat(bundleGroup.latestBundle.symbolicName);
    }
  }

  onDeleteBundleGroup(event: Event, bundleGroup: BundleGroup) {
    event.stopPropagation();
    this.deleteBundle.emit(bundleGroup.latestBundle);
  }
}
